import { verbatim } from '@myre/common-i18n';
import { type Currency } from '@myre/app-am-generated/types/Currency';
import { type ScaleFactor } from '@myre/common-primitives';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import {
  BuildChangeStrategy,
  FormComponent,
  QuestionControlService,
  QuestionDropdown,
} from '@myre/ui-primitive-forms';

import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';

import { WORDINGS } from '../../myre-wordings-loader';

@Component({
  imports: [FormComponent, FormsModule],
  selector: 'myui-dropdown-factor',
  standalone: true,
  templateUrl: './dropdown-factor.component.html',
  styleUrls: ['./dropdown-factor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyuiDropdownFactorComponent {
  private readonly _questionControlService = inject(QuestionControlService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  public readonly currency = input<Currency | null>(null);

  public readonly currentFactor = input<ScaleFactor>(1 as ScaleFactor);

  public readonly onChange = output<ScaleFactor>();

  public factorElements: QuestionDropdown[] = [];
  public factorFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  constructor() {
    effect(() => {
      this._buildForm(this.currency(), this.currentFactor());
    });
  }

  private _buildForm(
    currency: Currency | null,
    currentFactor: ScaleFactor,
  ): void {
    if (!currency) {
      this.factorElements = [];
      this.factorFormGroup = new UntypedFormGroup({});

      return;
    }

    const wordings = this._wordings._library.dropdownFactor;
    const bindings = { currency: currency.code };

    this.factorElements = [
      new QuestionDropdown({
        key: 'factor',
        defaultValue: 1,
        initialValue: currentFactor,
        options: [
          {
            label: verbatim(currency.code),
            value: 1,
          },
          {
            label: wordings.thousands({ bindings }),
            value: 1e3,
          },
          {
            label: wordings.millions({ bindings }),
            value: 1e6,
          },
        ],
        required: true,
        buildChangeStrategy: BuildChangeStrategy.SkipBuild,
        onValueChange: (value, _, __, initiator) => {
          if (initiator) {
            return;
          }

          const factorValue = +value as ScaleFactor;

          if (currentFactor !== factorValue) {
            this.onChange.emit(factorValue);
          }
        },
      }),
    ];

    this.factorFormGroup = this._questionControlService.generateForm(
      this.factorElements,
    );
  }
}
